import React from 'react';
import { motion } from 'framer-motion'; // Added for potential animation

const AvailablePlans = ({ plans = [], onSelectPlan, isLoading }) => { // Added isLoading prop
  return (
    <div className="mb-8 transform-gpu transition-medium hover:scale-[1.005]">
      <h2 className="text-2xl font-bold mb-5 flex items-center group">
        <div className="w-10 h-10 rounded-lg bg-accent-blue/20 flex items-center justify-center mr-3 transform-gpu group-hover:rotate-12 transition-medium">
            <i className="fas fa-layer-group text-accent-blue"></i>
        </div>
        Available Plans
        <div className="ml-3 text-xs py-1 px-2 bg-accent-blue/20 rounded-full text-accent-blue opacity-0 group-hover:opacity-100 transition-medium">
          {plans.length} options
        </div>
      </h2>

      {plans.length === 0 && !isLoading ? (
         <div className="text-center text-gray-400 py-6">No plans available.</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {plans.map((plan, index) => (
            <motion.div // Optional animation per card
              key={plan.id || index} // Use plan.id if available
              initial={{ opacity: 0, y: 10 }}
               animate={{ opacity: 1, y: 0 }}
               transition={{ delay: index * 0.05 }}
               className={`bg-dark-600 rounded-2xl p-6 flex flex-col relative group
                 ${ plan.isCurrent
                     ? 'border-2 border-accent-purple premium-glow' // Existing glow
                     : 'border border-dark-400 hover:border-accent-blue/30 overflow-hidden' // Apply overflow-hidden only to non-current cards
                 }
                ${plan.cardGradientClass || ''}
                ${!plan.isCurrent && plan.name !== 'Free' && !isLoading ? 'cursor-pointer' : 'cursor-default'}
                ${isLoading ? 'opacity-70 pointer-events-none' : ''} // Dim and disable if parent is loading
              `}
               onClick={() => !plan.isCurrent && plan.name !== 'Free' && !isLoading ? onSelectPlan(plan) : null} // Disable click when loading
             >
                {/* Current Plan Badge - z-20 to ensure it's above card border/content if needed */}
               {plan.isCurrent && (
                 <div className="absolute -top-3 right-4 bg-gradient-to-r from-accent-purple to-accent-blue text-xs px-2 py-1 rounded-full font-semibold z-20 transform-gpu hover:scale-110 transition-medium">
                   CURRENT PLAN
                 </div>
               )}
                {/* Corner flourish */}
                <div className="absolute -bottom-4 -right-4 w-16 h-16 opacity-10 bg-white rounded-full blur-xl group-hover:opacity-20 transition-medium"></div>
              {/* Background glow on hover */}
               <div className={`absolute -inset-1 rounded-xl blur opacity-0 transition-medium group-hover:opacity-100
                   ${plan.name === 'Premium' ? 'bg-gradient-to-r from-accent-purple/10 to-accent-blue/10' : ''}
                   ${plan.name === 'Professional' ? 'bg-gradient-to-r from-accent-pink/10 to-accent-orange/10' : ''}
                   ${plan.name === 'VIP' ? 'bg-gradient-to-r from-accent-yellow/10 to-accent-green/10' : ''}
               `}></div>


              <div className="relative z-10"> {/* Ensure content is above background effects */}
                {/* Plan Icon, Title, Description */}
                <div className="mb-4 group/title">
                   <div className="flex items-center mb-2 transform-gpu group-hover/title:-translate-y-0.5 transition-medium">
                     <div className={`w-10 h-10 rounded-lg flex items-center justify-center mr-3 transform-gpu group-hover/title:rotate-6 transition-medium
                       ${ plan.isCurrent
                          ? `${plan.iconBgColor} ${plan.applyPulseToIcon ? 'premium-pulse' : ''}`
                          : plan.iconBgColor || 'bg-gray-500/20'
                       }`
                     }>
                       <i className={`fas ${plan.icon || 'fa-question-circle'} ${
                        plan.isCurrent && plan.name !== 'Free' ? plan.iconTextColor : (plan.iconTextColor || 'text-gray-400')
                       } group-hover/title:scale-110 transition-medium`}></i>
                     </div>
                     <h3 className={`text-xl font-bold transition-colors ${
                       plan.name === 'Premium' ? 'gradient-text-premium' :
                       plan.name === 'Professional' ? 'gradient-text-pro' : 'group-hover/title:text-white'
                     } group-hover/title:animate-pulse-text`}>
                       {plan.name}
                     </h3>
                   </div>
                   <p className="text-gray-400 text-sm group-hover/title:text-gray-300 transition-medium">{plan.description}</p>
                 </div>

                 {/* Price */}
                 <div className="mb-6 transform-gpu group-hover:scale-[1.03] transition-medium">
                    <p className="text-3xl font-bold group-hover:text-accent-purple transition-medium">
                        {plan.price}
                        <span className="text-sm text-gray-400 group-hover:text-gray-300 transition-medium">{plan.period}</span>
                    </p>
                 </div>
              </div>


              {/* Features List - Use flex-grow */}
              <div className="space-y-3 mb-6 flex-grow relative z-10">
                {plan.features?.map((feature, idx) => ( // Use optional chaining
                  <div key={`feature-${idx}`} className="flex items-start group/feature">
                    <i className="fas fa-check text-accent-green mr-2 mt-1 text-sm group-hover/feature:scale-125 transition-medium"></i>
                    <span className="text-sm group-hover/feature:text-white transition-medium">{feature}</span>
                  </div>
                ))}
                {plan.missingFeatures?.map((feature, idx) => ( // Use optional chaining
                  <div key={`missing-${idx}`} className="flex items-start group/feature">
                    <i className="fas fa-times text-accent-red mr-2 mt-1 text-sm group-hover/feature:scale-125 transition-medium"></i>
                    <span className="text-sm text-gray-500 group-hover/feature:text-gray-400 transition-medium">{feature}</span>
                  </div>
                ))}
              </div>

              {/* Button - Push to bottom */}
              <div className="mt-auto relative z-10"> {/* Ensure button is on top */}
                <button
                  onClick={(e) => { // Prevent event propagation if needed
                      e.stopPropagation();
                      if (!plan.isCurrent && plan.name !== 'Free' && !isLoading) onSelectPlan(plan);
                  }}
                  disabled={plan.isCurrent || plan.name === 'Free' || isLoading} // Disable if loading
                  className={`w-full py-2.5 rounded-xl text-sm font-medium transition-all mt-auto transform-gpu hover:-translate-y-0.5 active:translate-y-0 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:translate-y-0 disabled:hover:shadow-none
                    ${ plan.isCurrent
                        ? 'bg-gradient-to-r from-accent-purple to-accent-blue text-white cursor-default opacity-70' // Style for current
                        : plan.name === 'Professional'
                            ? 'bg-gradient-to-r from-accent-pink to-accent-orange text-white hover:opacity-90 hover:shadow-glow-pink'
                            : plan.name === 'Free'
                                ? 'bg-dark-500 text-gray-400 cursor-default opacity-70' // Style for Free tier button
                                : 'bg-dark-500 text-gray-300 hover:bg-dark-400 hover:text-white' // Default/VIP button
                    }
                  `}
                >
                  {plan.isCurrent ? 'Current Plan' : plan.name === 'Free' ? 'Free Tier' : `Upgrade to ${plan.name}`}
                </button>
              </div>

            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AvailablePlans;